import {
  AnimaliaDetailFieldDTO,
  AnimaliaDetailPillDTO,
  AnimaliaDetailTaxonomyStepDTO,
} from '../../../../application/mappers/AnimaliaDetailMapper';
import { buildAnimaliaTaxonomyCanonicalRoute } from '../../../../application/taxonomy/AnimaliaNavigableTaxonomy';
import { animaliaDetailDesignTokens } from '../shared/animalia-detail-design-tokens';

export class MvAnimaliaDetailFields extends HTMLElement {
  private sectionTitle = '';

  private kicker = '';

  private fields: ReadonlyArray<AnimaliaDetailFieldDTO> = [];

  private featuredCard: AnimaliaDetailTaxonomyStepDTO | AnimaliaDetailPillDTO | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  setSection(
    kicker: string,
    title: string,
    fields: ReadonlyArray<AnimaliaDetailFieldDTO>,
    featuredCard: AnimaliaDetailTaxonomyStepDTO | AnimaliaDetailPillDTO | null = null,
  ): void {
    this.kicker = kicker;
    this.sectionTitle = title;
    this.fields = [...fields];
    this.featuredCard = featuredCard;
    this.render();
  }

  connectedCallback(): void {
    this.render();
  }

  private render(): void {
    if (!this.shadowRoot) {
      return;
    }

    this.shadowRoot.innerHTML = `
      <style>
        ${animaliaDetailDesignTokens}

        .section-head {
          margin: 0 0 18px;
        }

        h2 {
          margin: 8px 0 0;
          color: #31463d;
          font-size: clamp(1.8rem, 2.6vw, 2.8rem);
          font-family: Georgia, 'Times New Roman', serif;
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 16px;
        }

        .featured-card {
          margin: 0 0 16px;
          padding: 18px 20px;
          background: linear-gradient(135deg, rgba(95, 120, 128, 0.96), rgba(49, 70, 61, 0.98));
          border: 1px solid transparent;
        }

        .featured-link {
          display: block;
          text-decoration: none;
        }

        .info-card {
          padding: 18px;
          min-height: 118px;
        }

        strong,
        a {
          display: block;
          margin-top: 8px;
          color: #31463d;
          line-height: 1.55;
          text-decoration: none;
          word-break: break-word;
        }

        .featured-card strong,
        .featured-card .field-label,
        .featured-card .final-mark {
          color: #f7f3ed;
        }

        .final-mark {
          display: inline-flex;
          margin-top: 10px;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          font-size: 0.73rem;
        }

        @media (max-width: 980px) {
          .info-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 720px) {
          .info-card {
            padding-left: 16px;
            padding-right: 16px;
          }
        }
      </style>

      <section>
        <header class="section-head">
          <p class="section-kicker">${this.escapeHtml(this.kicker)}</p>
          <h2>${this.escapeHtml(this.sectionTitle)}</h2>
        </header>
        ${this.featuredCard
          ? `
              <a class="featured-link" href="${this.escapeAttribute(
                buildAnimaliaTaxonomyCanonicalRoute(this.featuredCard.filterKey, this.featuredCard.value),
              )}">
                <article class="featured-card editorial-panel">
                  <p class="field-label">${this.escapeHtml(this.featuredCard.label)}</p>
                  <strong>${this.escapeHtml(this.featuredCard.value)}</strong>
                  <span class="final-mark">nivel final</span>
                </article>
              </a>
            `
          : ''}
        <div class="info-grid">
          ${this.fields
            .map(
              (field) => `
                <article class="info-card editorial-panel">
                  <p class="field-label">${this.escapeHtml(field.label)}</p>
                  ${field.href
                    ? `<a href="${this.escapeAttribute(field.href)}" target="_blank" rel="noreferrer">${this.escapeHtml(field.value)}</a>`
                    : `<strong>${this.escapeHtml(field.value)}</strong>`}
                </article>
              `,
            )
            .join('')}
        </div>
      </section>
    `;
  }

  private escapeHtml(value: string): string {
    return value
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  private escapeAttribute(value: string): string {
    return this.escapeHtml(value);
  }
}

if (!customElements.get('mv-animalia-detail-fields')) {
  customElements.define('mv-animalia-detail-fields', MvAnimaliaDetailFields);
}
